import { ImageWDefault } from '@/components/image-w-default';
import { NavButton } from '@/components/nav-button';
import { RoundedBadge } from '@/components/rounded-badge';
import { DialogBody, DialogContent, DialogRoot, DialogTrigger } from '@/components/ui/dialog';
import { AppRoute, ROYCO_DOMAIN_URL } from '@/constants/shared';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { App } from '@/types';
import { capitalizeFirstLetter, getBannerImgPathFromAppId, getIconPathFromAppId, isTruthy } from '@/utils';
import {
  Box,
  Button,
  Card,
  CardRootProps,
  DialogHeader,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useRef } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import { useSearchParams } from 'react-router';
import { AppDescriptionCard } from './AppDescriptionCard';
import { PPBanner } from './PlumePointsBanner';
import { filterableProperties } from './constants';

const AppCardIcons = ({ app }: { app: App }) => {
  if (!app.isLive) {
    return (
      <RoundedBadge color="fg.subtle" py={2} alignSelf="start">
        Coming Soon
      </RoundedBadge>
    );
  }
  const icons = filterableProperties
    .filter((p) => p.filterFn(app))
    .map((p) => <Image h={6} key={p.name} src={p.icon} />);
  return <HStack alignSelf="start">{icons}</HStack>;
};

export const AppModal = ({
  app,
  variant = 'default',
  modalId,
}: {
  app: App;
  variant?: 'small' | 'default';
  modalId: string;
} & CardRootProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeModalId = searchParams.get('modalId');
  const isOpen = activeModalId === modalId;
  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (details.open) newSearchParams.set('modalId', modalId);
      else newSearchParams.delete('modalId');
      setSearchParams(newSearchParams);
    },
    [modalId, searchParams, setSearchParams],
  );

  const {
    id,
    title,
    description,
    category,
    doesCountForPoints,
    hasRoycoMarket,
    hasOptimizerVault,
    x,
    website,
    marketingWebsite,
    isLive,
  } = app;
  return (
    <DialogRoot size="xl" initialFocusEl={() => ref.current} onOpenChange={handleOpenChange} open={isOpen}>
      <DialogTrigger
        asChild
        onClick={() =>
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalClickedApp, {
            app_name: title,
          })
        }
      >
        <Card.Root
          key={title + category}
          cursor={isLive ? 'pointer' : 'disabled'}
          _hover={isLive ? { transform: 'scale(0.985)', transition: 'transform 0.3s' } : {}}
          borderRadius={'2xl'}
          pointerEvents={isLive ? 'all' : 'none'}
          bgColor={isLive ? 'white' : 'bg.subtle'}
          w="full"
          h={variant === 'small' ? 'auto' : 236}
        >
          {variant === 'small' ? (
            <HStack gap={4} py={4} px={6}>
              <ImageWDefault
                src={getIconPathFromAppId(id)}
                defaultSrc={'/assets/apps/default-app-icon.avif'}
                h={12}
                w={12}
              />
              <VStack gap={1} alignItems="flex-start">
                <Heading fontSize="lg" fontWeight="500" lineHeight="24px" whiteSpace="nowrap">
                  {title}
                </Heading>
                <Text fontWeight="500" lineHeight="24px" color="text.secondary">
                  {category}
                </Text>
              </VStack>
            </HStack>
          ) : (
            <>
              <Card.Header>
                <Flex justifyContent="space-between">
                  <ImageWDefault
                    src={getIconPathFromAppId(id)}
                    defaultSrc={'/assets/apps/default-app-icon.avif'}
                    h={12}
                    w={12}
                  />
                  <AppCardIcons app={app} />
                </Flex>
              </Card.Header>
              <Card.Body display="flex" flexDir="column">
                <Card.Title fontFamily="Lufga, sans-serif">{title}</Card.Title>
                <Card.Description>
                  <Text lineClamp={2}>{description}</Text>
                </Card.Description>
                <RoundedBadge color="fg.muted" px={3} py={1.5} mt={'auto'} w="fit-content">
                  {capitalizeFirstLetter(category)}
                </RoundedBadge>
              </Card.Body>
            </>
          )}
        </Card.Root>
      </DialogTrigger>
      <DialogContent borderRadius={'3xl'} p={2} ref={ref}>
        <Button
          bg="bg"
          size="xs"
          color="fg.muted"
          position="absolute"
          _hover={{
            bgColor: 'bg.emphasized',
          }}
          top={6}
          right={6}
          h={8}
          w={8}
          rounded="full"
          onClick={() => handleOpenChange({ open: false })}
        >
          <IoClose />
        </Button>
        <DialogHeader borderTopRadius="2xl" p={0} overflow="hidden">
          <ImageWDefault
            src={getBannerImgPathFromAppId(id)}
            defaultSrc={'/assets/apps/banners/default-banner.avif'}
            objectFit="cover"
            objectPosition="center"
            w="full"
            h={{
              base: 83,
              md: 220,
            }}
          />
        </DialogHeader>
        <DialogBody
          p={8}
          spaceY={8}
          px={{
            base: 0,
            lg: 8,
          }}
        >
          <HStack justifyContent="space-between" mb={6}>
            <Flex gap={4}>
              <ImageWDefault
                src={getIconPathFromAppId(id)}
                defaultSrc={'/assets/apps/default-app-icon.avif'}
                h={{
                  base: '64px',
                  lg: '99px',
                }}
                w={{
                  base: '64px',
                  lg: '99px',
                }}
              />
              <Flex justifyContent="space-evenly" flexDir={'column'} hideBelow="lg">
                <HStack gap={4}>
                  <Heading size="4xl" fontWeight="700">
                    {title}
                  </Heading>
                </HStack>
                <Box>
                  <RoundedBadge color="fg.muted">{capitalizeFirstLetter(category)}</RoundedBadge>
                </Box>
              </Flex>
            </Flex>
            <HStack alignSelf={['center', 'center', 'flex-start']} mr={2} gap={2}>
              {x && (
                <NavButton variant="outline" px={0} href={x} target="_blank">
                  <Image src="/assets/x.svg" />
                </NavButton>
              )}
              {marketingWebsite && (
                <NavButton variant="outline" px={0} href={marketingWebsite} target="_blank">
                  <Image src="/assets/website.svg" />
                </NavButton>
              )}
              {website && (
                <NavButton direction="none" variant="solid" href={website} target="_blank">
                  Launch App
                  <FiArrowUpRight />
                </NavButton>
              )}
            </HStack>
          </HStack>
          <VStack gap={2} alignItems="flex-start">
            <Heading hideFrom="lg" fontSize="40px" fontWeight="700" lineHeight="48px">
              {title}
            </Heading>
            <Text fontWeight={500} fontSize="lg" fontFamily="Lufga, sans-serif" color="fg.muted">
              {description}
            </Text>
          </VStack>
          <VStack>
            {doesCountForPoints && <PPBanner appName={app.title} backgroundSize={['cover', 'cover', 'contain']} />}
            {(hasRoycoMarket || hasOptimizerVault) && (
              <AppDescriptionCard
                title="PLUME Incentives"
                icon="/assets/tokens/plume.svg"
                description="Deposit into Optimizer or Royco Markets"
                ctas={[
                  hasRoycoMarket && (
                    <NavButton key="royco" href={ROYCO_DOMAIN_URL}>
                      Royco {<FiArrowUpRight />}
                    </NavButton>
                  ),
                  // hasOptimizerVault && (
                  //   <NavButton key="optimizer" direction="fwd" href={AppRoute.Optimizer}>
                  //     Optimizer
                  //   </NavButton>
                  // ),
                ].filter(isTruthy)}
              />
            )}
            {app.quests.map((quest) => (
              <AppDescriptionCard
                key={quest.id}
                title={quest.title}
                description={quest.description}
                ctas={[
                  <NavButton direction="fwd" href={`${AppRoute.Quests}/${quest.id}`}>
                    Level Up
                  </NavButton>,
                ]}
                icon="/assets/quests/map.svg"
              />
            ))}
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};
